import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

interface ButtonProps {
  children: React.ReactNode;
  to?: string;
  href?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
}

const Button: React.FC<ButtonProps> = ({
  children,
  to,
  href,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  onClick,
  type = 'button',
}) => {
  if (to) {
    return (
      <StyledLink 
        to={to} 
        variant={variant} 
        size={size} 
        fullWidth={fullWidth}
      >
        {children}
      </StyledLink>
    );
  }

  if (href) {
    return (
      <StyledAnchor 
        href={href} 
        variant={variant} 
        size={size} 
        fullWidth={fullWidth}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </StyledAnchor>
    );
  }

  return (
    <StyledButton 
      variant={variant} 
      size={size} 
      fullWidth={fullWidth}
      onClick={onClick}
      type={type}
    >
      {children}
    </StyledButton>
  );
};

const buttonStyles = css<{
  variant: 'primary' | 'secondary' | 'outline';
  size: 'small' | 'medium' | 'large';
  fullWidth: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-secondary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 0;
  transition: var(--transition);
  cursor: pointer;
  width: ${({ fullWidth }) => fullWidth ? '100%' : 'auto'};
  
  ${({ size }) => {
    switch (size) {
      case 'small':
        return css`
          padding: 0.8rem 1.6rem;
          font-size: 1.2rem;
        `;
      case 'large':
        return css`
          padding: 1.6rem 3.2rem;
          font-size: 1.8rem;
        `;
      default:
        return css`
          padding: 1.2rem 2.4rem;
          font-size: 1.4rem;
        `;
    }
  }}
  
  ${({ variant }) => {
    switch (variant) {
      case 'secondary':
        return css`
          background-color: var(--color-secondary);
          color: var(--color-light);
          border: none;
          
          &:hover {
            background-color: var(--color-primary);
            transform: translateY(-3px);
          }
        `;
      case 'outline':
        return css`
          background-color: transparent;
          color: var(--color-primary);
          border: 2px solid var(--color-primary);
          
          &:hover {
            background-color: var(--color-primary);
            color: var(--color-light);
            transform: translateY(-3px);
          }
        `;
      default:
        return css`
          background-color: var(--color-accent);
          color: var(--color-light);
          border: none;
          
          &:hover {
            background-color: var(--color-primary);
            transform: translateY(-3px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
          }
        `;
    }
  }}
`;

const StyledButton = styled.button<{
  variant: 'primary' | 'secondary' | 'outline';
  size: 'small' | 'medium' | 'large';
  fullWidth: boolean;
}>`
  ${buttonStyles}
`;

const StyledLink = styled(Link)<{
  variant: 'primary' | 'secondary' | 'outline';
  size: 'small' | 'medium' | 'large';
  fullWidth: boolean;
}>`
  ${buttonStyles}
  text-decoration: none;
`;

const StyledAnchor = styled.a<{
  variant: 'primary' | 'secondary' | 'outline';
  size: 'small' | 'medium' | 'large';
  fullWidth: boolean;
}>`
  ${buttonStyles}
  text-decoration: none;
`;

export default Button;
